import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const SUGGESTIONS_BY_MODE = {
  'text-to-game': [
    { id: 'trailer', icon: 'Clapperboard', label: 'Create a Trailer', desc: 'Auto-generate a promo trailer', color: '#6366F1', action: 'trailer' },
    { id: 'thumbnail', icon: 'Image', label: 'Create Thumbnail', desc: 'Generate a game thumbnail', color: '#8B5CF6', action: 'thumbnail' },
    { id: 'music', icon: 'Music', label: 'Add Background Music', desc: 'Generate matching game music', color: '#10B981', action: 'music' },
    { id: 'social', icon: 'Share2', label: 'Create Social Clip', desc: 'Short clip for social media', color: '#EC4899', action: 'social' },
  ],
  'prompt-to-mini-game': [
    { id: 'trailer', icon: 'Clapperboard', label: 'Create a Trailer', desc: 'Auto-generate a promo trailer', color: '#6366F1', action: 'trailer' },
    { id: 'music', icon: 'Music', label: 'Add Background Music', desc: 'Generate matching game music', color: '#10B981', action: 'music' },
    { id: 'social', icon: 'Share2', label: 'Create Social Clip', desc: 'Short clip for social media', color: '#EC4899', action: 'social' },
    { id: 'publish', icon: 'Globe', label: 'Publish to Community', desc: 'Share with LoopNest community', color: '#F59E0B', action: 'publish' },
  ],
  'text-to-animation': [
    { id: 'music', icon: 'Music', label: 'Generate Background Music', desc: 'Add music to your animation', color: '#10B981', action: 'music' },
    { id: 'game', icon: 'Gamepad2', label: 'Turn Into a Game', desc: 'Convert animation to game level', color: '#6366F1', action: 'game' },
    { id: 'social', icon: 'Share2', label: 'Create Social Clip', desc: 'Short clip for social media', color: '#EC4899', action: 'social' },
    { id: 'publish', icon: 'Globe', label: 'Publish to Community', desc: 'Share with LoopNest community', color: '#F59E0B', action: 'publish' },
  ],
  'script-to-video': [
    { id: 'trailer', icon: 'Clapperboard', label: 'Create a Trailer', desc: 'Generate a short promo trailer', color: '#6366F1', action: 'trailer' },
    { id: 'thumbnail', icon: 'Image', label: 'Create Thumbnail', desc: 'Generate a video thumbnail', color: '#8B5CF6', action: 'thumbnail' },
    { id: 'music', icon: 'Music', label: 'Add Background Music', desc: 'Generate matching music', color: '#10B981', action: 'music' },
    { id: 'social', icon: 'Share2', label: 'Create Social Clip', desc: 'Short clip for social media', color: '#EC4899', action: 'social' },
  ],
  'music-to-video': [
    { id: 'trailer', icon: 'Clapperboard', label: 'Create a Trailer', desc: 'Generate a promo trailer', color: '#6366F1', action: 'trailer' },
    { id: 'social', icon: 'Share2', label: 'Create Social Clip', desc: 'Short clip for social media', color: '#EC4899', action: 'social' },
    { id: 'publish', icon: 'Globe', label: 'Publish to Community', desc: 'Share with LoopNest community', color: '#F59E0B', action: 'publish' },
    { id: 'game', icon: 'Gamepad2', label: 'Turn Into a Game', desc: 'Use this as game background music', color: '#8B5CF6', action: 'game' },
  ],
  'text-to-music': [
    { id: 'video', icon: 'Video', label: 'Create Music Video', desc: 'Generate visuals for this track', color: '#6366F1', action: 'video' },
    { id: 'social', icon: 'Share2', label: 'Create Social Clip', desc: 'Short clip for social media', color: '#EC4899', action: 'social' },
    { id: 'game', icon: 'Gamepad2', label: 'Use in a Game', desc: 'Add this as game background music', color: '#10B981', action: 'game' },
    { id: 'publish', icon: 'Globe', label: 'Publish to Community', desc: 'Share with LoopNest community', color: '#F59E0B', action: 'publish' },
  ],
};

export default function PostGenerationSuggestions({ creation, onAction, onDismiss }) {
  const [dismissed, setDismissed] = useState(false);
  const [actioned, setActioned] = useState(new Set());

  if (!creation || dismissed) return null;

  const suggestions = SUGGESTIONS_BY_MODE?.[creation?.mode] || SUGGESTIONS_BY_MODE?.['text-to-game'];

  const handleAction = (suggestion) => {
    setActioned((prev) => new Set([...prev, suggestion.id]));
    onAction?.(suggestion?.action, creation);
  };

  return (
    <div className="rounded-2xl overflow-hidden animate-fade-in-up" style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-md)' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--color-border)', background: 'rgba(99,102,241,0.04)' }}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-primary)' }}>
            <Icon name="Lightbulb" size={13} color="white" />
          </div>
          <p className="text-sm font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>What's Next?</p>
          <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(99,102,241,0.1)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>AI Suggestions</span>
        </div>
        <button
          onClick={() => { setDismissed(true); onDismiss?.(); }}
          className="w-6 h-6 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all"
        >
          <Icon name="X" size={12} color="var(--color-muted-foreground)" />
        </button>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {suggestions?.map((suggestion) => {
          const done = actioned?.has(suggestion?.id);
          return (
            <button
              key={suggestion?.id}
              onClick={() => !done && handleAction(suggestion)}
              className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all hover:opacity-80"
              style={{
                borderColor: done ? 'rgba(16,185,129,0.4)' : `${suggestion?.color}30`,
                background: done ? 'rgba(16,185,129,0.06)' : `${suggestion?.color}08`,
              }}
            >
              <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: done ? 'rgba(16,185,129,0.15)' : `${suggestion?.color}18` }}>
                <Icon name={done ? 'Check' : suggestion?.icon} size={14} color={done ? 'var(--color-success)' : suggestion?.color} />
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold" style={{ color: done ? 'var(--color-success)' : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{suggestion?.label}</p>
                <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: 9 }}>{suggestion?.desc}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
